import type { MusicOutput } from './audio';
// Szenen-Musik-Player fürs Live-Gespräch (Live-Gespräch v3, Spez §7).
//
// Ein einzelnes HTMLAudioElement (loop) spielt die vom Server per
// {"type":"music"}-Frame gewählte Untermalung. Lautstärke-Choreografie:
// - Fade-in 1,5 s beim Start, Fade-out 1 s beim Stop (linearer RAF-Ramp),
// - Ducking: spricht die KI oder der User, sinkt das Ziel weich auf 0,08
//   (sonst Grundlautstärke 0,25) — setTargetAtTime-artiges Lerp, kein Sprung,
// - Track-Wechsel: alten Track ausfaden, dann den neuen einfaden.
// Effektive Lautstärke = fade (0..1) × geglättete Ducking-Lautstärke.
//
// iOS: play() läuft innerhalb der bereits laufenden Session (die User-Geste
// war der Session-Start) — der .play()-Promise wird trotzdem defensiv
// gefangen (Autoplay-Ablehnung darf die Session nie stören, Musik ist
// optionale Untermalung).

export const MUSIC_BASE_VOLUME = 0.25;
export const MUSIC_DUCKED_VOLUME = 0.08;
export const MUSIC_FADE_IN_MS = 1500;
export const MUSIC_FADE_OUT_MS = 1000;
// Zeitkonstante des Ducking-Lerps (~250 ms bis dicht am Ziel).
const DUCK_LERP_TAU_MS = 250;
// Fallback-Takt, falls requestAnimationFrame fehlt (Tests/Sonderfälle).
const TICK_FALLBACK_MS = 16;
// Hintergrund-Takt (Review 07/2026): rAF friert bei verstecktem Dokument/
// gesperrtem Display komplett ein — Ducking/stop() wären dann wirkungslos
// (Musik loopt in voller Lautstärke weiter). Dann treibt ein Timeout den
// Tick. Browser drosseln Hintergrund-Timer auf ~1 s — für die Fade-/Stop-
// Choreografie reicht das (etwas gröber, aber stop()/Ducking greifen).
const BACKGROUND_TICK_MS = 250;
// rAF-Watchdog: feuerte das angeforderte rAF so lange nicht (Display wurde
// direkt nach der Planung gesperrt/der Tab versteckt), übernimmt der Timeout.
const RAF_WATCHDOG_MS = 500;
// dt-Deckel der Timeout-getriebenen Ticks: im Hintergrund soll die
// Choreografie in Echtzeit voranschreiten (1-s-Drossel-Ticks dürfen also
// ~1 s Fade-Fortschritt machen), ein Riesen-Sprung nach langem Stillstand
// bleibt trotzdem gedeckelt. Der rAF-Pfad behält den engen 100-ms-Deckel —
// im Vordergrund ändert sich nichts.
const BACKGROUND_DT_CAP_MS = 1500;

// ─── Pure Helfer (exportiert für Vitest) ─────────────────────────────────────

// Ziel-Lautstärke je Ducking-Zustand (nie lauter als die Basis).
export function computeMusicVolume(ducked: boolean, base = MUSIC_BASE_VOLUME): number {
  return ducked ? Math.min(base, MUSIC_DUCKED_VOLUME) : base;
}

// Absolute Musik-URL: API_BASE + Server-Pfad; leere Base → relativ
// (same-origin im Web-Build). Der Server liefert bereits encodierte Pfade.
export function resolveMusicUrl(base: string, url: string): string {
  if (!url) return '';
  const trimmed = (base || '').replace(/\/+$/, '');
  if (!trimmed) return url;
  return `${trimmed}${url.startsWith('/') ? '' : '/'}${url}`;
}

// Linearer Fade-Schritt: fade (0..1) Richtung 'in' (1,5 s) bzw. 'out' (1 s).
export function stepFade(fade: number, direction: 'in' | 'out', dtMs: number): number {
  const dt = Math.max(0, dtMs);
  if (direction === 'in') return Math.min(1, fade + dt / MUSIC_FADE_IN_MS);
  return Math.max(0, fade - dt / MUSIC_FADE_OUT_MS);
}

// Weicher Lerp-Schritt Richtung target (exponentielle Annäherung, wie
// setTargetAtTime) — frame-raten-tolerant über dtMs/tauMs.
export function stepTowards(current: number, target: number, dtMs: number, tauMs = DUCK_LERP_TAU_MS): number {
  if (!(tauMs > 0)) return target;
  const k = Math.min(1, Math.max(0, dtMs) / tauMs);
  // k == 1 → exakt das Ziel (Float-Drift von current + delta*1 vermeiden).
  if (k >= 1) return target;
  return current + (target - current) * k;
}

// ─── Player ──────────────────────────────────────────────────────────────────

export interface LiveMusicPlayer {
  // Track starten (Fade-in); läuft schon einer → erst aus-, dann einfaden.
  play(url: string, title: string): void;
  // Fade-out, danach Pause (idempotent).
  stop(): void;
  // Echte Pause: haelt das Element an OHNE Fade-Sweep und OHNE den Track zu
  // vergessen. resume() nimmt an derselben Stelle wieder auf. Fuer den
  // Pausenknopf der Klangbuehne und die Verdraengung durch ein Live-Gespraech —
  // stop()+play() wuerde den Titel jedes Mal von Sekunde 0 neu laden.
  pause(): void;
  // Fortsetzen nach pause() (idempotent, No-Op ohne pausierten Track).
  resume(): void;
  // Ducking-Ziel setzen (weiches Lerp, kein Sprung).
  setDucked(ducked: boolean): void;
  // Lautstärke-Skala 0..1 (Nutzer-Schieberegler). Wirkt multiplikativ auf
  // die Basis-Lautstärke 0,25 — 0 = stumm, 1 = voll. Default 1.
  setVolume(scale: number): void;
  // Hart an/aus (Nutzer-Schalter): false = sofortige Stille (kein
  // Fade-Out-Sweep), true = sofort wieder hörbar gemäß aktueller Lautstärke
  // und Ducking. Fade/Direction bleiben unangetastet — das Element läuft
  // weiter im Hintergrund, nur die Hörbarkeit kippt.
  setEnabled(enabled: boolean): void;
  prime(): void;
  // Aktueller Track-Titel (null = keine Musik).
  getTitle(): string | null;
  // Kompletter Abbau — MUSS in jedem Teardown-Pfad laufen.
  dispose(): void;
}

interface PendingTrack {
  url: string;
  title: string;
}

export function createLiveMusicPlayer(
  getBaseUrl: () => string,
  attachOutput?: (element: HTMLAudioElement) => MusicOutput,
): LiveMusicPlayer {
  const audio = new Audio();
  audio.loop = true;
  audio.preload = 'auto';
  audio.crossOrigin = 'anonymous';
  const output = attachOutput?.(audio);
  audio.volume = output ? 1 : 0;
  const applyVolume = (value: number) => {
    if (output) output.setVolume(value);
    else audio.volume = value;
  };
  applyVolume(0);

  let disposed = false;
  let fade = 0;
  let direction: 'in' | 'out' = 'out';
  // Geglättete Ducking-Lautstärke (lerpt Richtung computeMusicVolume(ducked)).
  let duck = MUSIC_BASE_VOLUME;
  let ducked = false;
  // Nutzer-Schalter (Music on/off im Overlay-Einstellungsmenü): false setzt
  // die effektive Lautstärke sofort auf 0, lässt Fade/Direction aber
  // unverändert → beim Wiedereinschalten ist die Choreografie intakt.
  let enabled = true;
  // Echte Pause (pause()/resume()): das Element steht, Fade/Ducking/Track
  // bleiben unangetastet — der Tick-Loop ruht so lange.
  let paused = false;
  // Nutzer-Lautstärke 0..1 (Schieberegler): multiplikativ auf die Basis
  // 0,25, Default 1. Greift sowohl ungeduckt als auch im Ducking-Zustand.
  let volumeScale = 1;
  let title: string | null = null;
  let currentUrl: string | null = null;
  // Track-Wechsel: nächster Track wartet, bis der alte ausgefadet ist.
  let pending: PendingTrack | null = null;
  let rafId: number | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastTick = 0;

  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

  const cancelTick = () => {
    if (rafId !== null) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const scheduleTick = () => {
    if (disposed || rafId !== null || timeoutId !== null) return;
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    if (typeof requestAnimationFrame === 'function' && !hidden) {
      rafId = requestAnimationFrame(() => {
        rafId = null;
        // Der rAF hat übernommen → Watchdog abbestellen.
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        tick();
      });
      // Watchdog: wird das Dokument DIREKT nach der Planung versteckt/das
      // Display gesperrt, feuert das rAF nie — der Timeout übernimmt dann,
      // und die Folge-Ticks laufen über den Hidden-Zweig weiter.
      timeoutId = setTimeout(() => {
        timeoutId = null;
        if (rafId !== null) {
          if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
          rafId = null;
        }
        tick(BACKGROUND_DT_CAP_MS);
      }, RAF_WATCHDOG_MS);
      return;
    }
    // Hidden (oder kein rAF vorhanden): Timeout-getrieben — im Vordergrund
    // ändert dieser Zweig nichts, dort läuft weiter der rAF.
    timeoutId = setTimeout(() => {
      timeoutId = null;
      tick(hidden ? BACKGROUND_DT_CAP_MS : undefined);
    }, hidden ? BACKGROUND_TICK_MS : TICK_FALLBACK_MS);
  };

  const startTrack = (track: PendingTrack) => {
    paused = false;
    currentUrl = track.url;
    // Titel erst NACH erfolgreichem play() setzen — getTitle() meldet sonst
    // Musik, die nie zu hören ist (z. B. Autoplay-Ablehnung auf iOS).
    title = null;
    fade = 0;
    direction = 'in';
    applyVolume(0);
    audio.src = resolveMusicUrl(getBaseUrl(), track.url);
    // iOS/Autoplay: Ablehnung still schlucken — Musik ist optional. Bei
    // Rejection den Zustand komplett zurücksetzen (kein hängender Titel,
    // kein loopendes Element mit gesetzter src).
    const playResult: unknown = audio.play();
    if (playResult instanceof Promise) {
      playResult.then(
        () => {
          // Zwischenzeitlicher Track-Wechsel/stop()/dispose() → nicht mehr
          // unser Start, Titel nicht übernehmen.
          if (disposed || currentUrl !== track.url || direction !== 'in') return;
          title = track.title || null;
        },
        () => {
          // Läuft schon ein Wechsel (direction 'out' mit ggf. pending), regelt
          // der Tick-Loop den Übergang — hier nur den eigenen Start abräumen.
          if (disposed || currentUrl !== track.url || direction !== 'in') return;
          currentUrl = null;
          title = null;
          pending = null;
          fade = 0;
          direction = 'out';
          cancelTick();
          try {
            audio.pause();
          } catch {
            /* egal */
          }
          try {
            audio.removeAttribute('src');
            audio.load();
          } catch {
            /* egal */
          }
        },
      );
    } else {
      // Kein Promise (alte Browser/Testumgebungen) → sofort übernehmen.
      title = track.title || null;
    }
    lastTick = now();
    scheduleTick();
  };

  const tick = (dtCapMs = 100) => {
    if (disposed || paused) return;
    const t = now();
    // dt deckeln (Tab-Hintergrund/RAF-Pause soll keinen Sprung erzeugen);
    // Timeout-getriebene Hintergrund-Ticks reichen einen höheren Deckel
    // durch, damit die Choreografie dort in Echtzeit voranschreitet.
    const dt = Math.max(0, Math.min(dtCapMs, t - lastTick));
    lastTick = t;

    // Ducking-Ziel: enabled=false zwingt das Ziel auf 0 (Ducking/Lautstärke
    // wird damit weich auf 0 gezogen — kein Knacken beim Ausschalten).
    // Andernfalls Musik-Basis × Nutzer-Skala (computeMusicVolume kümmert sich
    // bereits um das Ducking-Deckel-Verhältnis).
    const target = enabled ? computeMusicVolume(ducked) * volumeScale : 0;
    duck = stepTowards(duck, target, dt);
    fade = stepFade(fade, direction, dt);
    // Hard-off-by-enabled: zusätzlicher Multiplikator — falls enabled per
    // Direkt-Setter kippt, ohne dass das Ducking-Ziel den Hörbereich erreicht
    // hat (seltene Race-Fälle während rAF-Pause), hört die Musik sofort auf.
    try {
      applyVolume(Math.max(0, Math.min(1, fade * duck * (enabled ? 1 : 0))));
    } catch {
      /* egal — jsdom/Sonderfälle */
    }

    if (direction === 'out' && fade === 0) {
      // Ausfade fertig: anhalten; wartet ein Track-Wechsel → neuen einfaden.
      try {
        audio.pause();
      } catch {
        /* egal */
      }
      if (pending) {
        const next = pending;
        pending = null;
        startTrack(next);
      } else {
        currentUrl = null;
        title = null;
      }
      return;
    }
    scheduleTick();
  };

  return {
    play(url: string, trackTitle: string) {
      if (disposed || !url) return;
      const next: PendingTrack = { url, title: trackTitle || '' };
      // `paused` zaehlt als aktiv: ein erneutes play() desselben Titels (der
      // Abgleich-Effekt der Klangbuehne ruft das bei jedem Zustandswechsel)
      // darf eine echte Pause weder aufheben noch den Track neu starten.
      const active = direction === 'in' && (paused || !audio.paused);
      if (active && currentUrl === url) {
        // Gleicher Track läuft schon → nur den Titel übernehmen.
        title = next.title || null;
        return;
      }
      if (paused) {
        // Anderer Titel bei stehender Pause: es ist nichts zu hören, also gibt
        // es auch nichts zu überblenden — sofort starten statt 1 s Leer-Fade.
        pending = null;
        startTrack(next);
        return;
      }
      if (active) {
        // Track-Wechsel: alten ausfaden, neuen danach einfaden.
        pending = next;
        direction = 'out';
        lastTick = now();
        scheduleTick();
        return;
      }
      pending = null;
      startTrack(next);
    },
    stop() {
      if (disposed) return;
      pending = null;
      title = null;
      // Ein echter Stopp hebt eine stehende Pause auf — sonst koennte der
      // ruhende Tick-Loop den Ausfade nie zu Ende fahren.
      paused = false;
      if (direction === 'out' && fade === 0) return;
      direction = 'out';
      lastTick = now();
      scheduleTick();
    },
    pause() {
      if (disposed || paused || !currentUrl) return;
      paused = true;
      // Tick-Loop anhalten: Fade/Ducking sollen exakt so stehen bleiben, wie
      // sie sind — beim resume() geht es genau dort weiter.
      cancelTick();
      try {
        audio.pause();
      } catch {
        /* egal */
      }
    },
    resume() {
      if (disposed || !paused) return;
      paused = false;
      if (!currentUrl) return;
      const result: unknown = audio.play();
      if (result instanceof Promise) result.catch(() => undefined);
      lastTick = now();
      scheduleTick();
    },
    setDucked(next: boolean) {
      if (disposed) return;
      ducked = next;
      // Der Tick-Loop läuft während der Wiedergabe ohnehin und lerpt hin.
    },
    setVolume(scale: number) {
      if (disposed) return;
      // NaN/negativ/unsinnig → Default 1. Dann auf 0..1 klemmen.
      const safe = Number.isFinite(scale) ? scale : 1;
      volumeScale = Math.max(0, Math.min(1, safe));
    },
    setEnabled(next: boolean) {
      if (disposed) return;
      enabled = !!next;
      if (!enabled) applyVolume(0);
      // Tick laeuft ohnehin (solange ein Track spielt, fading laeuft) und
      // zieht den audio.volume-Wert nach. Reicht hier, keine Plan-Funktion
      // aufzurufen — der Wechsel ist innerhalb von ~250 ms (tau) gehoerbar.
    },
    prime() {
      if (disposed || currentUrl) return;
      // Unlock this media element in the same gesture as the shared context.
      // A silent WAV is local, unbilled, and never becomes a displayed track.
      audio.src = 'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQIAAAAAAA==';
      void audio.play().catch(() => undefined);
    },
    getTitle() {
      return title;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      output?.dispose();
      pending = null;
      title = null;
      currentUrl = null;
      cancelTick();
      try {
        audio.pause();
      } catch {
        /* egal */
      }
      try {
        audio.removeAttribute('src');
        audio.load();
      } catch {
        /* egal */
      }
    },
  };
}
